// Reference: https://uploadcare.com/docs/processing/

interface IAutoRotate {
  autoRotate: boolean
}
interface IPreview {
  preview: string
}
interface IProgressive {
  progressive: boolean
}
interface IResize {
  resize: string
}
interface IScaleCrop {
  scaleCrop: string
}
interface IFormat {
  format: string
}
interface IQuality {
  quality: string
}
interface IStretch {
  stretch: string
}
interface IBlur {
  blur: number
}

export type Transformation =
  | IAutoRotate
  | IPreview
  | IProgressive
  | IResize
  | IScaleCrop
  | IFormat
  | IQuality
  | IStretch
  | IBlur

export default function applyTransformations(url: string, transformations: Transformation[]) {
  const parts = transformations
    .map(transformation => {
      if (isAutoRotate(transformation)) {
        return partForAutoRotate(transformation.autoRotate)
      }

      if (isPreview(transformation)) {
        return partForPreview(transformation.preview)
      }

      if (isProgressive(transformation)) {
        return partForProgressive(transformation.progressive)
      }

      if (isResize(transformation)) {
        return partForResize(transformation.resize)
      }

      if (isScaleCrop(transformation)) {
        return partForScaleCrop(transformation.scaleCrop)
      }

      if (isFormat(transformation)) {
        return partForFormat(transformation.format)
      }

      if (isQuality(transformation)) {
        return partForQuality(transformation.quality)
      }

      if (isStretch(transformation)) {
        return partForStretch(transformation.stretch)
      }

      if (isBlur(transformation)) {
        return partForBlur(transformation.blur)
      }

      throw new Error(`received invalid uploadcare transformation ${transformation}`)
    })
    .join('/-/')

  return parts ? `${url}-/${parts}/` : url
}

function partForAutoRotate(autoRotate: boolean) {
  return `autorotate/${autoRotate ? 'yes' : 'no'}`
}

function partForPreview(preview: string) {
  return `preview${preview && `/${preview}`}`
}

function partForProgressive(progressive: boolean) {
  return `progressive/${progressive ? 'yes' : 'no'}`
}

function partForResize(resize: string) {
  return `resize/${resize}`
}

function partForScaleCrop(scaleCrop: string) {
  return `scale_crop/${scaleCrop}/center`
}

function partForFormat(format: string) {
  return `format/${format}`
}

function partForQuality(quality: string) {
  return `quality/${quality}`
}

function partForStretch(stretch: string) {
  return `stretch/${stretch}`
}

function partForBlur(blur: number) {
  return `blur/${blur}`
}

function isAutoRotate(transformation: Transformation): transformation is IAutoRotate {
  return (transformation as IAutoRotate).autoRotate !== undefined
}

function isPreview(transformation: Transformation): transformation is IPreview {
  return (transformation as IPreview).preview !== undefined
}

function isProgressive(transformation: Transformation): transformation is IProgressive {
  return (transformation as IProgressive).progressive !== undefined
}

function isResize(transformation: Transformation): transformation is IResize {
  return (transformation as IResize).resize !== undefined
}

function isScaleCrop(transformation: Transformation): transformation is IScaleCrop {
  return (transformation as IScaleCrop).scaleCrop !== undefined
}

function isFormat(transformation: Transformation): transformation is IFormat {
  return (transformation as IFormat).format !== undefined
}

function isQuality(transformation: Transformation): transformation is IQuality {
  return (transformation as IQuality).quality !== undefined
}

function isStretch(transformation: Transformation): transformation is IStretch {
  return (transformation as IStretch).stretch !== undefined
}

function isBlur(transformation: Transformation): transformation is IBlur {
  return (transformation as IBlur).blur !== undefined
}
